import { DEFAULT_CMS_LANGUAGE } from '@app/constants/cms';
import { isDefined, isNull } from '@app/utils/is';
import { isEmpty } from '@app/utils/isEmpty';
import { report } from '@app/utils/monitoring';
import { Cms, Translations } from '@typings';
import { mergeWith, zipWith } from 'ramda';

const EMPTY_SETTINGS = { settings: {} };

export const localizationMerger = (left: unknown, right: unknown): unknown => {
  if (Array.isArray(left) && Array.isArray(right)) {
    return zipWith(localizationMerger, left, right);
  }

  if (isNull(left) || isNull(right)) {
    return right;
  }

  if (typeof left === 'object' && typeof right === 'object') {
    return mergeWith(localizationMerger, left, right);
  }

  return right;
};

export const getBlockLocalization = (
  id: string,
  lang: Translations.SupportedLanguagesCodes,
  allLocalizations: Partial<
    Record<
      Translations.SupportedLanguagesCodes,
      {
        blocks: Cms.LocalizedBlockSettingsRecord;
      }
    >
  >,
) => {
  return allLocalizations[lang]?.blocks[id] ?? EMPTY_SETTINGS;
};

export const localizeBlockItem =
  (
    lang: Translations.SupportedLanguagesCodes,
    allLocalizations: Partial<
      Record<
        Translations.SupportedLanguagesCodes,
        {
          blocks: Cms.LocalizedBlockSettingsRecord;
        }
      >
    >,
  ) =>
  (blockData: Cms.AnyBlock) => {
    const defaultLanguageLocalization = getBlockLocalization(blockData.id, DEFAULT_CMS_LANGUAGE, allLocalizations);
    const customLanguageLocalization = getBlockLocalization(blockData.id, lang, allLocalizations);

    return localizeBlock({
      defaultBlockData: blockData,
      defaultLanguageLocalization,
      language: lang,
      localizedBlockData: customLanguageLocalization,
    });
  };

export const localizeBlock = ({
  language,
  defaultBlockData,
  defaultLanguageLocalization,
  localizedBlockData,
}: {
  language: Translations.SupportedLanguagesCodes;
  defaultBlockData: Nullable<Cms.AnyBlock>;
  defaultLanguageLocalization: Cms.LocalizedBlockSettings;
  localizedBlockData: Cms.LocalizedBlockSettings;
}) => {
  const localizedDefaultBlockData = applyBlockLocalization(defaultBlockData, defaultLanguageLocalization);
  if (language === DEFAULT_CMS_LANGUAGE) {
    return localizedDefaultBlockData;
  }

  return applyBlockLocalization(localizedDefaultBlockData, localizedBlockData);
};

export const applyBlockLocalization = (
  defaultBlockData: Nullable<Cms.AnyBlock>,
  localizedBlockData: Cms.LocalizedBlockSettings,
): Nullable<Cms.AnyBlock> => {
  if (!isDefined(defaultBlockData) || isEmpty(localizedBlockData.settings)) {
    return defaultBlockData;
  }

  if (isEmpty(defaultBlockData.settings) && !isEmpty(localizedBlockData.settings)) {
    report(`Block ${defaultBlockData.id} is empty, and there are localizations for this block in the page data, indicating data mismatch.`);

    return defaultBlockData;
  }

  return mergeWith(localizationMerger, defaultBlockData, localizedBlockData);
};
